/**
 * ProfilePage — "Mis datos" (Sprint 14.0).
 *
 * Pantalla de SOLO LECTURA del perfil del atleta. Resuelve el feedback del
 * beta tester: poder VER sus datos (especialmente si tiene reloj) sin tener
 * que rehacer el onboarding ni borrar el plan.
 *
 * Lee el perfil de `GET /onboarding/status` (vía `getOnboardingStatus`, ya
 * existente) — CERO backend nuevo. La edición real (con regeneración
 * condicional del plan) es un sub-bloque dedicado de Sprint 14; aquí solo
 * se muestra una nota honesta de "próximamente".
 *
 * Estados: loading · error (con reintentar) · sin perfil (CTA completar) ·
 * perfil con campos null → "Sin especificar" (no rompe).
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { getOnboardingStatus } from "@/api/onboarding";
import {
  updateProfile,
  updateTrainingProfile,
  type UpdateProfilePayload,
  type UpdateTrainingPayload,
} from "@/api/profile";
import { DeleteAccountSection } from "@/components/profile/DeleteAccountSection";
import { Button } from "@/components/ui/Button";
import { FormError } from "@/components/ui/FormError";
import { Input } from "@/components/ui/Input";
import { t, translateError } from "@/i18n/es";
import { ApiError, type AthleteProfile, type DeviceType } from "@/types/api";

// Sprint 14 v12 Bloque A: rango aceptado de FC máx (espejo del backend).
const HR_MAX_MIN = 40;
const HR_MAX_MAX = 230;
// Umbral del aviso NO bloqueante "valor inusualmente bajo".
const HR_MAX_LOW_WARNING = 150;

// v12 Bloque B2: opciones de los selectores de campos que regeneran el plan.
const LEVEL_OPTIONS = [
  { value: "beginner", label: t.profile.levelBeginner },
  { value: "amateur", label: t.profile.levelAmateur },
];
const SURFACE_OPTIONS = [
  { value: "asfalto", label: t.profile.surfaceAsfalto },
  { value: "trail", label: t.profile.surfaceTrail },
  { value: "mixto", label: t.profile.surfaceMixto },
];
const DAYS_OPTIONS = [
  { value: "2", label: "2" },
  { value: "3", label: "3" },
  { value: "4", label: "4" },
];
// v16.3 §3: dispositivo editable. Cambiar device_type regenera el plan
// (reorienta gates v16.2 §6 + inyección test_vam día 1 si vam_m_per_min IS NULL).
const DEVICE_OPTIONS = [
  { value: "watch_with_hr", label: t.profile.deviceWatchHr },
  { value: "watch_no_hr", label: t.profile.deviceWatchNoHr },
  { value: "mobile_only", label: t.profile.deviceMobileOnly },
];

/** device_type → texto legible (el dato que el tester buscaba). */
function _deviceLabel(device: AthleteProfile["device_type"] | null): string {
  switch (device) {
    case "watch_with_hr":
      return t.profile.deviceWatchHr;
    case "watch_no_hr":
      return t.profile.deviceWatchNoHr;
    case "mobile_only":
      return t.profile.deviceMobileOnly;
    default:
      return t.profile.notSpecified;
  }
}

function _levelLabel(level: AthleteProfile["level"]): string {
  if (level === "beginner") return t.profile.levelBeginner;
  if (level === "amateur") return t.profile.levelAmateur;
  return t.profile.notSpecified;
}

function _surfaceLabel(surface: AthleteProfile["surface_primary"]): string {
  switch (surface) {
    case "asfalto":
      return t.profile.surfaceAsfalto;
    case "trail":
      return t.profile.surfaceTrail;
    case "mixto":
      return t.profile.surfaceMixto;
    case "pista":
      return t.profile.surfacePista;
    default:
      return t.profile.notSpecified;
  }
}

/** Segundos/km → "m:ss /km". Null → "Sin especificar". */
function _paceLabel(secondsPerKm: number | null): string {
  if (secondsPerKm == null || secondsPerKm <= 0) return t.profile.notSpecified;
  const min = Math.floor(secondsPerKm / 60);
  const sec = secondsPerKm % 60;
  return `${min}:${String(sec).padStart(2, "0")} ${t.profile.paceUnit}`;
}

function _dateLabel(iso: string | null): string {
  if (!iso) return t.profile.notSpecified;
  // Mostrar solo la fecha (YYYY-MM-DD → DD/MM/YYYY local-safe sin Date()).
  const datePart = iso.slice(0, 10);
  const [y, m, d] = datePart.split("-");
  return d && m && y ? `${d}/${m}/${y}` : datePart;
}

interface Row {
  label: string;
  value: string;
  testid: string;
}

/**
 * Sesión 33 EXTENDIDA — Alba feature (Conversación 2 Alberto).
 *
 * Toggle UX-only para agenda flexible: cuando true, `SessionCard` muestra
 * "Sesión N" en lugar del día de la semana. NO regenera plan ni cambia
 * `session_date` en backend (preserva matching Strava).
 *
 * Independiente del modo edición global del perfil — decisión rápida del
 * atleta, un click + toast.
 */
interface FlexibleSchedulingToggleProps {
  profile: AthleteProfile;
  onUpdated: (profile: AthleteProfile) => void;
}

function FlexibleSchedulingToggle({
  profile,
  onUpdated,
}: FlexibleSchedulingToggleProps) {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const current = profile.flexible_scheduling === true;

  async function onToggle() {
    setSaving(true);
    setErr(null);
    try {
      const updated = await updateProfile({ flexible_scheduling: !current });
      onUpdated(updated);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      setErr(code ? translateError(code) : t.profile.saveError);
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      data-testid="profile-flexible-scheduling-section"
      className="flex flex-col gap-2 rounded-md border border-border-subtle bg-carbon-raised p-4"
    >
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-bone">
            {t.profile.flexibleSchedulingLabel}
          </span>
          <span className="text-xs text-bone-muted">
            {t.profile.flexibleSchedulingHelp}
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={current}
          data-testid="profile-flexible-scheduling-toggle"
          disabled={saving}
          onClick={onToggle}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-fast focus:outline-none focus:ring-2 focus:ring-lime focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
            current ? "bg-lime" : "bg-border-strong"
          }`}
        >
          <span
            aria-hidden="true"
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-bone shadow ring-0 transition duration-fast ${
              current ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>
      {err && <FormError message={err} />}
    </section>
  );
}

export function ProfilePage() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<AthleteProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Modo edición unificado (v12 Bloque A: FC/zapatilla · Bloque B2:
  // nivel/superficie/días, que regeneran el plan).
  const [editing, setEditing] = useState(false);
  const [hrMaxInput, setHrMaxInput] = useState("");
  const [shoeInput, setShoeInput] = useState("");
  const [levelInput, setLevelInput] = useState("");
  const [surfaceInput, setSurfaceInput] = useState("");
  const [daysInput, setDaysInput] = useState("");
  const [deviceInput, setDeviceInput] = useState("");
  const [hrMaxErr, setHrMaxErr] = useState<string | undefined>();
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Modal de confirmación de regeneración (B2 §3) + aviso post-regen.
  const [showRegenModal, setShowRegenModal] = useState(false);
  const [regenNotice, setRegenNotice] = useState<string | null>(null);

  function _enterEdit(p: AthleteProfile) {
    setHrMaxInput(p.hr_max != null ? String(p.hr_max) : "");
    setShoeInput(p.shoe ?? "");
    setLevelInput(p.level ?? "");
    setSurfaceInput(
      p.surface_primary === "pista" ? "" : (p.surface_primary ?? ""),
    );
    setDaysInput(
      p.training_days_per_week != null ? String(p.training_days_per_week) : "",
    );
    setDeviceInput(p.device_type ?? "");
    setHrMaxErr(undefined);
    setSaveErr(null);
    setShowRegenModal(false);
    setRegenNotice(null);
    setEditing(true);
  }

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const status = await getOnboardingStatus();
      setProfile(status.profile);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Valida la FC máx tecleada. Devuelve false (+ setea error) si inválida. */
  function _validateHr(): boolean {
    const trimmed = hrMaxInput.trim();
    if (trimmed === "") return true;
    const n = Number(trimmed);
    if (!Number.isInteger(n) || n < HR_MAX_MIN || n > HR_MAX_MAX) {
      setHrMaxErr(translateError("hr_max_out_of_range"));
      return false;
    }
    return true;
  }

  /** Payload con SOLO los campos sin-regeneración que cambiaron. */
  function _nonRegenPayload(p: AthleteProfile): UpdateProfilePayload {
    const out: UpdateProfilePayload = {};
    const trimmedHr = hrMaxInput.trim();
    const origHr = p.hr_max != null ? String(p.hr_max) : "";
    // FC: solo si cambió y no se vació (limpiar FC no está soportado por el API).
    if (trimmedHr !== origHr && trimmedHr !== "") out.hr_max = Number(trimmedHr);
    const trimmedShoe = shoeInput.trim();
    if (trimmedShoe !== (p.shoe ?? "")) out.shoe = trimmedShoe || null;
    return out;
  }

  /** Payload con SOLO los campos con-regeneración que cambiaron. */
  function _regenPayload(p: AthleteProfile): UpdateTrainingPayload {
    const out: UpdateTrainingPayload = {};
    if (levelInput && levelInput !== (p.level ?? "")) {
      out.level = levelInput as UpdateTrainingPayload["level"];
    }
    const origSurface = p.surface_primary === "pista" ? "" : (p.surface_primary ?? "");
    if (surfaceInput && surfaceInput !== origSurface) {
      out.surface_primary = surfaceInput as UpdateTrainingPayload["surface_primary"];
    }
    const origDays = p.training_days_per_week != null ? String(p.training_days_per_week) : "";
    if (daysInput && daysInput !== origDays) {
      out.training_days_per_week = Number(daysInput);
    }
    if (deviceInput && deviceInput !== (p.device_type ?? "")) {
      out.device_type = deviceInput as DeviceType;
    }
    return out;
  }

  // Submit del form: decide si guardar directo (solo no-regen) o pedir
  // confirmación (cualquier campo de regeneración tocado).
  async function onSave() {
    if (profile === null) return;
    setSaveErr(null);
    setHrMaxErr(undefined);
    if (!_validateHr()) return;

    const regen = _regenPayload(profile);
    const nonRegen = _nonRegenPayload(profile);
    const hasRegen = Object.keys(regen).length > 0;
    const hasNonRegen = Object.keys(nonRegen).length > 0;

    if (!hasRegen && !hasNonRegen) {
      setEditing(false); // nada cambió
      return;
    }
    if (hasRegen) {
      // Hay regeneración → confirmación explícita (un solo modal).
      setShowRegenModal(true);
      return;
    }
    // Solo campos sin-regeneración → guardar directo (Bloque A).
    await _runSave(nonRegen, {});
  }

  // Confirmar el modal → ejecutar el guardado con regeneración.
  async function _confirmRegen() {
    if (profile === null) return;
    await _runSave(_nonRegenPayload(profile), _regenPayload(profile));
  }

  /** Orquesta las dos llamadas: primero no-regen (rápido), luego regen.
   *  Si la 2ª falla tras guardar la 1ª → error de guardado PARCIAL claro. */
  async function _runSave(
    nonRegen: UpdateProfilePayload,
    regen: UpdateTrainingPayload,
  ) {
    setSaving(true);
    setSaveErr(null);
    const hasRegen = Object.keys(regen).length > 0;
    let nonRegenSaved = false;
    try {
      if (Object.keys(nonRegen).length > 0) {
        await updateProfile(nonRegen);
        nonRegenSaved = true;
      }
      let warning: string | null = null;
      if (hasRegen) {
        const res = await updateTrainingProfile(regen);
        warning = res.generation_warning ?? null;
      }
      setShowRegenModal(false);
      await refresh();
      setRegenNotice(warning);
      setEditing(false);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : null;
      // Guardado PARCIAL: el no-regen YA se guardó pero la regeneración falló.
      if (nonRegenSaved && hasRegen) {
        setSaveErr(t.profile.savePartialError);
      } else {
        setSaveErr(code ? translateError(code) : t.profile.saveError);
      }
      // Mantenemos el modal abierto (si lo estaba) para que el user vea el
      // error y pueda reintentar o cancelar.
    } finally {
      setSaving(false);
    }
  }

  /** Render de un grupo de radios "pill" para los campos de regeneración. */
  function _radioGroup(args: {
    label: string;
    testid: string;
    value: string;
    options: { value: string; label: string }[];
    onChange: (v: string) => void;
  }) {
    return (
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-xs uppercase tracking-widest text-gray-mid">
          {args.label}
        </legend>
        <div className="flex flex-wrap gap-2" data-testid={args.testid}>
          {args.options.map((o) => {
            const selected = args.value === o.value;
            return (
              <label
                key={o.value}
                className={`cursor-pointer rounded-md border px-3 py-1.5 text-sm ${
                  selected
                    ? "border-lime bg-lime/10 text-bone"
                    : "border-border-subtle text-bone-muted hover:border-border-strong"
                }`}
              >
                <input
                  type="radio"
                  name={args.testid}
                  value={o.value}
                  checked={selected}
                  onChange={() => args.onChange(o.value)}
                  className="sr-only"
                  data-testid={`${args.testid}-${o.value}`}
                />
                {o.label}
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  }

  const _shell = (children: React.ReactNode) => (
    <main
      data-testid="profile-page"
      className="mx-auto flex min-h-app max-w-md flex-col gap-6 bg-graphite p-6 text-bone"
    >
      <header>
        <p className="text-xs uppercase tracking-widest text-gray-mid">
          {t.profile.pageEyebrow}
        </p>
        <h1 className="mt-2 text-2xl font-medium tracking-tight">
          {t.profile.pageTitle}
        </h1>
      </header>
      {children}
      <div className="mt-auto">
        <Link to="/" className="text-xs text-bone-muted hover:text-bone">
          {t.profile.back}
        </Link>
      </div>
    </main>
  );

  if (loading) {
    return _shell(
      <p data-testid="profile-loading" className="text-sm text-bone-muted">
        {t.profile.loading}
      </p>,
    );
  }

  if (error) {
    return _shell(
      <div className="flex flex-col gap-3">
        <p data-testid="profile-error" className="text-sm text-danger">
          {t.profile.error}
        </p>
        <button
          type="button"
          onClick={() => void refresh()}
          className="self-start rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone"
        >
          {t.profile.retry}
        </button>
      </div>,
    );
  }

  if (profile === null) {
    return _shell(
      <div className="flex flex-col gap-3">
        <p data-testid="profile-empty" className="text-sm text-bone-muted">
          {t.profile.empty}
        </p>
        <button
          type="button"
          onClick={() => navigate("/onboarding")}
          className="self-start rounded-md bg-lime px-4 py-2.5 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90"
        >
          {t.profile.emptyCta}
        </button>
      </div>,
    );
  }

  // ---- Modo EDICIÓN unificado (B2: FC/zapatilla SIN regen + nivel/superficie/
  //      días CON regen marcados con ⚠️) ----
  if (editing) {
    const hrNum = Number(hrMaxInput.trim());
    const showLowWarning =
      hrMaxInput.trim() !== "" &&
      Number.isFinite(hrNum) &&
      hrNum >= HR_MAX_MIN &&
      hrNum < HR_MAX_LOW_WARNING;

    return _shell(
      <>
        <form
          data-testid="profile-edit-form"
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            void onSave();
          }}
          noValidate
        >
          <FormError message={saveErr} />

          {/* --- Campos SIN regeneración (FC máx + zapatilla) --- */}
          <div className="flex flex-col gap-1">
            <Input
              label={`${t.profile.fieldHrMax} (${t.profile.hrMaxUnit})`}
              type="number"
              inputMode="numeric"
              placeholder={t.profile.hrMaxEditPlaceholder}
              value={hrMaxInput}
              onChange={(e) => setHrMaxInput(e.target.value)}
              error={hrMaxErr}
              data-testid="profile-edit-hrmax"
            />
            {showLowWarning && (
              <p
                data-testid="profile-edit-hrmax-warning"
                className="text-xs text-bone-muted"
              >
                {t.profile.hrMaxLowWarning}
              </p>
            )}
          </div>
          <Input
            label={t.profile.fieldShoe}
            type="text"
            placeholder={t.profile.shoeEditPlaceholder}
            value={shoeInput}
            onChange={(e) => setShoeInput(e.target.value)}
            data-testid="profile-edit-shoe"
          />

          {/* --- Campos CON regeneración (⚠️) --- */}
          <div
            data-testid="profile-edit-regen-section"
            className="flex flex-col gap-4 rounded-lg border border-border-subtle bg-carbon-raised p-4"
          >
            <p className="text-xs text-bone-muted">
              ⚠️ {t.profile.regenSectionNote}
            </p>
            {_radioGroup({
              label: t.profile.fieldLevelEdit,
              testid: "profile-edit-level",
              value: levelInput,
              options: LEVEL_OPTIONS,
              onChange: setLevelInput,
            })}
            {_radioGroup({
              label: t.profile.fieldSurfaceEdit,
              testid: "profile-edit-surface",
              value: surfaceInput,
              options: SURFACE_OPTIONS,
              onChange: setSurfaceInput,
            })}
            {_radioGroup({
              label: t.profile.fieldDaysEdit,
              testid: "profile-edit-days",
              value: daysInput,
              options: DAYS_OPTIONS,
              onChange: setDaysInput,
            })}
            {/* v16.3 §3: dispositivo/reloj editable con regeneración. */}
            {_radioGroup({
              label: t.profile.fieldDeviceEdit,
              testid: "profile-edit-device",
              value: deviceInput,
              options: DEVICE_OPTIONS,
              onChange: setDeviceInput,
            })}
          </div>

          <div className="mt-1 flex items-center gap-3">
            <Button
              type="submit"
              loading={saving && !showRegenModal}
              data-testid="profile-edit-save"
            >
              {t.profile.saveButton}
            </Button>
            <button
              type="button"
              data-testid="profile-edit-cancel"
              onClick={() => setEditing(false)}
              className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone"
            >
              {t.profile.cancelButton}
            </button>
          </div>
        </form>

        {/* Modal de confirmación de regeneración (B2 §3). */}
        {showRegenModal && (
          <div
            data-testid="profile-regen-modal"
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-50 flex items-center justify-center bg-graphite/80 p-6"
          >
            <div className="w-full max-w-sm rounded-lg border border-border-subtle bg-carbon-raised p-6 text-bone">
              <h3 className="text-lg font-medium">{t.profile.regenModalTitle}</h3>
              <p className="mt-3 text-sm text-bone-muted">
                {t.profile.regenModalBody}
              </p>
              <div className="mt-4">
                <FormError message={saveErr} />
              </div>
              <div className="mt-5 flex items-center justify-end gap-3">
                <button
                  type="button"
                  data-testid="profile-regen-cancel"
                  onClick={() => setShowRegenModal(false)}
                  className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone"
                >
                  {t.profile.regenModalCancel}
                </button>
                <Button
                  type="button"
                  loading={saving}
                  onClick={() => void _confirmRegen()}
                  data-testid="profile-regen-confirm"
                >
                  {t.profile.regenConfirm}
                </Button>
              </div>
            </div>
          </div>
        )}
      </>,
    );
  }

  // ---- Modo LECTURA ----
  const rows: Row[] = [
    { label: t.profile.fieldLevel, value: _levelLabel(profile.level), testid: "profile-level" },
    { label: t.profile.fieldDevice, value: _deviceLabel(profile.device_type), testid: "profile-device" },
    { label: t.profile.fieldSurface, value: _surfaceLabel(profile.surface_primary), testid: "profile-surface" },
    {
      label: t.profile.fieldDays,
      value: profile.training_days_per_week != null
        ? String(profile.training_days_per_week)
        : t.profile.notSpecified,
      testid: "profile-days",
    },
    {
      label: t.profile.fieldHrMax,
      value: profile.hr_max != null
        ? `${profile.hr_max} ${t.profile.hrMaxUnit}`
        : t.profile.notSpecified,
      testid: "profile-hrmax",
    },
    { label: t.profile.fieldShoe, value: profile.shoe || t.profile.notSpecified, testid: "profile-shoe" },
    { label: t.profile.fieldVam, value: _paceLabel(profile.vam_pace_seconds_per_km), testid: "profile-vam" },
    { label: t.profile.fieldLastVamTest, value: _dateLabel(profile.last_vam_test_at), testid: "profile-last-vam" },
  ];

  return _shell(
    <>
      {/* B2: aviso de la regeneración (generation_warning del backend), ej.
          carrera muy próxima → plan comprimido. One-shot tras regenerar. */}
      {regenNotice && (
        <p
          data-testid="profile-regen-warning"
          className="rounded-md border border-border-subtle bg-carbon-raised px-4 py-3 text-sm text-bone-muted"
        >
          {regenNotice}
        </p>
      )}
      <dl className="flex flex-col divide-y divide-border-subtle rounded-lg border border-border-subtle bg-carbon-raised">
        {rows.map((r) => (
          <div
            key={r.testid}
            data-testid={r.testid}
            className="flex items-center justify-between gap-4 px-4 py-3"
          >
            <dt className="text-xs uppercase tracking-widest text-gray-mid">
              {r.label}
            </dt>
            <dd className="text-sm text-bone">{r.value}</dd>
          </div>
        ))}
      </dl>

      {/* v12: editar FC/zapatilla (directo) + nivel/superficie/días (regenera). */}
      <button
        type="button"
        data-testid="profile-edit-button"
        onClick={() => _enterEdit(profile)}
        className="self-start rounded-md bg-lime px-4 py-2 text-sm font-medium text-graphite transition-colors duration-fast hover:bg-lime/90"
      >
        {t.profile.editButton}
      </button>

      {/* Sesión 33 EXTENDIDA — Alba feature flexible scheduling.
          Toggle INDEPENDIENTE del modo edición (cambio UX-only, no
          regenera plan, decisión rápida del atleta). */}
      <FlexibleSchedulingToggle
        profile={profile}
        onUpdated={(updated) => setProfile(updated)}
      />

      {/* S14.5 (R3 GDPR): borrado de cuenta self-service. */}
      <DeleteAccountSection />
    </>,
  );
}
